import React, { createContext, FC, ReactNode, useState } from 'react';

import { usePersistState } from '../hooks/usePersistState';

// Interface defined in FE, if BE changes its response we need to update it.
export interface DecodedContext {
  lead: Lead;
  organization: Organization;
  personalDetails: PersonalDetails;
}

interface AddDeductOption {
  id: string;
  name: string;
}

export interface Range {
  from: number;
  to: number;
}

interface Lead {
  id: string;
  odometer: number;
  options: AddDeductOption[];
  range: Range;
  createdAt: string;
  decode: {
    make: string;
    model: string;
    provider: string;
    series: string;
    styleId: string;
    title: string;
    vin: string;
    year: number;
  };
}

interface PersonalDetails {
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
}

interface Organization {
  addressLine1: string;
  addressLine2: string;
  city: string;
  country: string;
  id: string;
  imageUrl: string;
  name: string;
  phone: string;
  state: string;
  zip: string;
}

interface VehicleData {
  year: number;
  make: string;
  model: string;
  style: string;
  series: string;
  vin: string;
  addDeductOptions: AddDeductOption[];
  odometer: number;
  fullName: string;
}

interface ValuationData {
  range: Range;
  createdAt: string;
}

export enum ValuationSummaryStepKey {
  INITIAL = 'Initial',
  EMAIL = 'Email',
  SMS = 'SMS',
  PHONE_CALL = 'PhoneCall',
}

export enum CodeValidationType {
  EMAIL,
  SMS,
  PHONE_CALL,
}

export interface ObfuscatedData {
  obfuscatedEmail: string;
  obfuscatedPhone: string;
}

export interface ValuationSummaryData {
  leadId: string;
  email: string;
  phoneNumber: string;
  valuationRange: Range;
  valuationDate: string;
  validationType?: CodeValidationType;
  obfuscatedData?: ObfuscatedData;
}

interface ValuationSummaryContextProps {
  organization: Organization;
  stepsData: ValuationSummaryData;
  setStepsData: React.Dispatch<React.SetStateAction<ValuationSummaryData>>;
  cognitoUserTemporal: any;
  setCognitoUserTemporal: any;
  vehicleData: VehicleData;
  userData: PersonalDetails;
  valuationData: ValuationData;
  parseContextData: (contextObject: DecodedContext) => void;
  resetSavedData: () => void;
  resendPhoneCallTimestamp: string;
  setResendPhoneCallTimestamp: React.Dispatch<React.SetStateAction<string>>;
}

interface ValuationSummaryProviderProps {
  children: ReactNode;
}

const ValuationSummaryContext = createContext<ValuationSummaryContextProps>(null);

const LOCALSTORAGE_PREFIX = 'vincar-vs-';

export const ValuationSummaryProvider: FC<ValuationSummaryProviderProps> = ({ children }) => {
  const [cognitoUserTemporal, setCognitoUserTemporal] = useState(null);
  const [organization, setOrganization] = usePersistState<Organization>(null, `${LOCALSTORAGE_PREFIX}organization`);
  const [stepsData, setStepsData] = usePersistState<ValuationSummaryData>(null, `${LOCALSTORAGE_PREFIX}stepsData`);
  const [vehicleData, setVehicleData] = usePersistState<VehicleData>(null, `${LOCALSTORAGE_PREFIX}vehicleData`);
  const [userData, setUserData] = usePersistState<PersonalDetails>(null, `${LOCALSTORAGE_PREFIX}userData`);
  const [valuationData, setValuationData] = usePersistState<ValuationData>(null, `${LOCALSTORAGE_PREFIX}valuationData`);
  const [resendPhoneCallTimestamp, setResendPhoneCallTimestamp] = usePersistState(
    null,
    `${LOCALSTORAGE_PREFIX}phonecallTimestamp`,
  );

  const resetSavedData = () => {
    setCognitoUserTemporal(null);
    setOrganization(null);
    setStepsData(null);
    setVehicleData(null);
    setUserData(null);
    setValuationData(null);
  };

  const parseContextData = (contextObject?: DecodedContext) => {
    if (!contextObject) {
      setOrganization(null);
      setStepsData(null);
      return setVehicleData(null);
    }
    setOrganization(contextObject.organization);
    setStepsData({
      leadId: contextObject.lead.id,
      email: contextObject.personalDetails.email,
      phoneNumber: contextObject.personalDetails.phoneNumber,
      valuationRange: contextObject.lead.range,
      valuationDate: contextObject.lead.createdAt ?? new Date().toISOString(),
    });
    setVehicleData({
      year: contextObject.lead.decode.year,
      make: contextObject.lead.decode.make,
      model: contextObject.lead.decode.model,
      style: contextObject.lead.decode.title,
      series: contextObject.lead.decode.series,
      vin: contextObject.lead.decode.vin,
      addDeductOptions: contextObject.lead.options,
      odometer: contextObject.lead.odometer,
      fullName: `${contextObject.lead.decode?.year} ${contextObject.lead.decode?.make} ${contextObject.lead.decode?.model} ${contextObject.lead.decode?.title} ${contextObject.lead.decode?.series}`,
    });
    setUserData({
      email: contextObject.personalDetails.email,
      firstName: contextObject.personalDetails.firstName,
      lastName: contextObject.personalDetails.lastName,
      phoneNumber: contextObject.personalDetails.phoneNumber,
    });
    setValuationData({
      range: contextObject.lead.range,
      createdAt: contextObject.lead.createdAt ?? new Date().toISOString(),
    });
  };

  return (
    <ValuationSummaryContext.Provider
      value={{
        organization,
        stepsData,
        setStepsData,
        cognitoUserTemporal,
        setCognitoUserTemporal,
        vehicleData,
        userData,
        valuationData,
        parseContextData,
        resetSavedData,
        resendPhoneCallTimestamp,
        setResendPhoneCallTimestamp,
      }}
    >
      {children}
    </ValuationSummaryContext.Provider>
  );
};

export const useValuationSummaryContext: () => ValuationSummaryContextProps = () => {
  return React.useContext(ValuationSummaryContext);
};
